import React, { useState, useEffect, useRef } from 'react';
import { getMessages, sendMessage, subscribeMessages, getTelegramChatId, getTelegramConnectUrl } from '../utils/chatService';
import './StudentChatModal.css';

// Captures the whole match so String.split keeps it in the result array,
// interleaved with the surrounding plain-text pieces (odd indices = URLs).
// The trailing negated class excludes common punctuation from the match so
// "check this out: https://x.com." doesn't swallow the sentence's period.
const URL_REGEX = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,!?;:'")\]}])/gi;

function linkifyText(text) {
  if (!text) return text;
  return String(text).split(URL_REGEX).map((part, i) => {
    if (i % 2 === 1) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <a
          key={i}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: 'inherit', textDecoration: 'underline', wordBreak: 'break-all' }}
        >
          {part}
        </a>
      );
    }
    return part;
  });
}

export default function StudentChatModal({ isOpen, onClose, studentId, studentName, senderName = '', isStudent = false }) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [tgLinked, setTgLinked] = useState(false);
  const messagesEndRef = useRef(null);
  const seenIds = useRef(new Set());

  // Load history, subscribe to new messages, check Telegram link
  useEffect(() => {
    if (!isOpen || !studentId) return;

    seenIds.current.clear();
    setLoading(true);

    getMessages(studentId).then((rows) => {
      rows.forEach((r) => seenIds.current.add(r.id));
      setMessages(rows);
      setLoading(false);
    }).catch((err) => {
      console.error('Chat getMessages error:', err);
      setLoading(false);
    });

    // Check Telegram link for current role
    getTelegramChatId(studentId, isStudent ? 'student' : 'teacher').then((id) => setTgLinked(!!id));

    const unsub = subscribeMessages(studentId, (row) => {
      if (seenIds.current.has(row.id)) return;
      seenIds.current.add(row.id);
      setMessages((prev) => [...prev, row]);
    });

    return unsub;
  }, [isOpen, studentId, isStudent]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim() || !studentId) return;

    const text = inputMessage.trim();
    setInputMessage('');

    try {
      const row = await sendMessage({
        studentId,
        senderRole: isStudent ? 'student' : 'teacher',
        senderName: mySenderName,
        text,
      });
      seenIds.current.add(row.id);
      setMessages((prev) => [...prev, row]);
    } catch (err) {
      console.error('Chat send error:', err);
    }
  };

  const mySenderName = senderName || (isStudent ? studentName || 'Student' : 'Teacher');

  const handleConnectTelegram = () => {
    const url = getTelegramConnectUrl(studentId, isStudent, mySenderName);
    if (url) window.open(url, '_blank');
  };

  const formatTime = (ts) =>
    new Date(ts).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });

  const dayKey = (ts) => {
    const d = new Date(ts);
    return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  };

  const formatDateSeparator = (ts) => {
    const d = new Date(ts);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(today.getDate() - 1);
    if (dayKey(ts) === dayKey(today)) return 'Today';
    if (dayKey(ts) === dayKey(yesterday)) return 'Yesterday';
    const sameYear = d.getFullYear() === today.getFullYear();
    return d.toLocaleDateString('en-US', sameYear
      ? { month: 'long', day: 'numeric' }
      : { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="chat-modal-backdrop" onClick={onClose} />
      <div
        className="chat-modal"
        // Desktop only (mobile forces bottom: 0 as a bottom sheet):
        // student dashboard's chat FAB sits at bottom-right (bottom 24,
        // 46px tall) — sit the modal directly above it. Teacher's
        // StudentProfile chat button is the upper one in a 2-button
        // vertical FAB stack, so its top edge is much higher — anchor
        // above the whole stack so it doesn't overlap Invite Link.
        style={{ '--chat-modal-bottom': isStudent ? '84px' : '190px' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0 4px' }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#333', fontWeight: 600 }}>
            Chat {studentName ? `with ${studentName}` : ''}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {/* Telegram connect button — both teacher and student */}
            {getTelegramConnectUrl(studentId, isStudent, mySenderName) && (
              <button
                data-tour="chat-telegram"
                onClick={handleConnectTelegram}
                title={tgLinked ? 'Telegram connected' : 'Connect Telegram to get messages in Telegram'}
                style={{
                  background: tgLinked ? '#e8f5e9' : '#e3f2fd',
                  border: 'none',
                  borderRadius: 6,
                  padding: '3px 8px',
                  fontSize: 11,
                  fontWeight: 600,
                  color: tgLinked ? '#2e7d32' : '#1565c0',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.248-2.012 9.49c-.145.658-.537.818-1.084.508l-3-2.21-1.447 1.394c-.16.16-.295.295-.605.295l.213-3.053 5.56-5.023c.242-.213-.054-.333-.373-.12l-6.871 4.326-2.962-.924c-.643-.204-.657-.643.136-.953l11.57-4.461c.537-.194 1.006.131.875.731z"/>
                </svg>
                {tgLinked ? 'Telegram ✓' : 'Telegram'}
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none', border: 'none', fontSize: 18, cursor: 'pointer',
                color: '#666', padding: 4, width: 24, height: 24,
                display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 4,
              }}
            >×</button>
          </div>
        </div>

        <div style={{ height: 1, background: '#ececec', margin: '2px 0' }} />

        {/* Messages */}
        <div className="chat-modal-messages" style={{
          flex: 1, overflowY: 'auto', padding: '8px 4px',
          display: 'flex', flexDirection: 'column', gap: 8,
          minHeight: 0,
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, padding: 20 }}>Loading…</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#999', fontSize: 14, padding: '40px 20px' }}>
              No messages yet. Start the conversation!
            </div>
          ) : (
            messages.map((msg, i) => {
              const mine = isStudent ? msg.sender_role === 'student' : msg.sender_role === 'teacher';
              const fromTelegram = msg.source === 'telegram';
              const prevMsg = messages[i - 1];
              const showDateSeparator = !prevMsg || dayKey(prevMsg.created_at) !== dayKey(msg.created_at);
              return (
                <React.Fragment key={msg.id}>
                  {showDateSeparator && (
                    <div style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#888',
                        background: '#f0f0f0',
                        borderRadius: 999,
                        padding: '3px 10px',
                      }}>
                        {formatDateSeparator(msg.created_at)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                    <div style={{
                      maxWidth: '75%',
                      padding: '8px 12px',
                      borderRadius: 12,
                      background: mine ? '#8a2be2' : '#f0f0f0',
                      color: mine ? '#fff' : '#333',
                      fontSize: 14,
                      wordBreak: 'break-word',
                    }}>
                      <div>{linkifyText(msg.text)}</div>
                      <div style={{ fontSize: 10, color: mine ? 'rgba(255,255,255,0.65)' : '#999', marginTop: 3, textAlign: 'right' }}>
                        {fromTelegram && <span style={{ marginRight: 4 }}>✈️</span>}
                        {formatTime(msg.created_at)}
                      </div>
                    </div>
                  </div>
                </React.Fragment>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, padding: '0 4px' }}>
            <input
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type a message..."
              style={{
                flex: 1, padding: '10px 12px',
                border: '1px solid #ddd', borderRadius: 8, fontSize: 14,
              }}
            />
            <button
              type="submit"
              disabled={!inputMessage.trim()}
              style={{
                padding: '10px 16px',
                background: inputMessage.trim() ? '#8a2be2' : '#ccc',
                color: '#fff', border: 'none', borderRadius: 8,
                cursor: inputMessage.trim() ? 'pointer' : 'not-allowed',
                fontWeight: 500, fontSize: 14, transition: 'background 0.22s',
              }}
            >
              Send
            </button>
          </div>
        </form>
      </div>
    </>
  );
}
